import { AuthContextProvider, ConfigContextProvider } from "@@components";
import { CssBaseline } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { SnackbarOrigin, SnackbarProvider } from "notistack";
import { useContext } from "react";
import "./App.css";
import ExpressionEvaluatorContextProvider from "./ExpressionsModule/Contexts/ExpressionEvaluatorContextProvider";
import "./styles/edit-mode.scss";
import { darkTheme } from "./themes/theme";
import DynamicRouteContainer from "./UserInterfaceModule/Containers/DynamicRouteContainer";
import BundleContext from "./UserInterfaceModule/Contexts/BundleContext";
import BundleProvider from "./UserInterfaceModule/Contexts/BundleProvider";
import { DesignerLiveChatProvider } from "./UserInterfaceModule/Contexts/DesignerLiveChatContext";
import { MaskingProvider } from "./UserInterfaceModule/Contexts/MaskingContext";
import NavigationHistoryProvider from "./UserInterfaceModule/Contexts/NavigationHistoryProvider";

dayjs.extend(utc);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const snackbarAnchorOrigin: SnackbarOrigin = { horizontal: "right", vertical: "bottom" };

function ThemedContent({ children }: React.PropsWithChildren) {
  const { Theme } = useContext(BundleContext);
  const theme = Theme ? createTheme(Theme) : darkTheme;
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}

function App() {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <QueryClientProvider client={queryClient}>
        <SnackbarProvider maxSnack={3} anchorOrigin={snackbarAnchorOrigin} preventDuplicate>
          <ConfigContextProvider>
            <AuthContextProvider>
              <DesignerLiveChatProvider>
                <BundleProvider>
                  <MaskingProvider>
                    <ThemedContent>
                      <NavigationHistoryProvider>
                        <ExpressionEvaluatorContextProvider logChanges={true}>
                          <DynamicRouteContainer />
                        </ExpressionEvaluatorContextProvider>
                      </NavigationHistoryProvider>
                    </ThemedContent>
                  </MaskingProvider>
                </BundleProvider>
              </DesignerLiveChatProvider>
            </AuthContextProvider>
          </ConfigContextProvider>
        </SnackbarProvider>
      </QueryClientProvider>
    </LocalizationProvider>
  );
}

export default App;
